import { cardPaint, weatherSky } from "@/components/shapes/shared/card-paint";
import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_DISPLAY,
  WIDGET_MUTED,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetRule,
  type WidgetPaint,
} from "@/components/shapes/shared/widget-face-parts";
import { useT } from "@/i18n";
import { weatherFrameMessages } from "@/i18n/messages/frames-weather";
import { cn } from "@/lib/cn";
import { previewSource } from "@/lib/frame-preview-source";
import { DayIcon } from "./weather-day-icon";
import { inferConditionIcon, type WeatherDay } from "./weather-days";
import { hasWeather, weatherFields, weatherRange } from "./weather-fields";

const MEDIUM_DAYS = 3;

/** Condition over its range, the range muted. */
function Summary({
  condition,
  range,
  className,
}: {
  condition: string | null;
  range: string;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      {condition && <p className="truncate">{condition}</p>}
      {range && <p className={cn("truncate", WIDGET_MUTED)}>{range}</p>}
    </div>
  );
}

/** Each day's low-to-high span on the week's scale. */
function DayBar({
  day,
  min,
  max,
}: {
  day: WeatherDay;
  min: number;
  max: number;
}) {
  const span = max - min || 1;
  return (
    <span className="relative h-[5px] rounded-[3px] bg-[var(--widget-fill,var(--card-line))]">
      <i
        className="absolute inset-y-0 rounded-[3px] bg-[var(--widget-ink,var(--card-ink))] opacity-[.85]"
        style={{
          left: `${((day.low - min) / span) * 100}%`,
          right: `${((max - day.high) / span) * 100}%`,
        }}
      />
    </span>
  );
}

/**
 * The weather on the desktop, in its sky's paint. Small: place, temperature,
 * condition and range. Medium: the next days beside it. Large: one row per
 * day with its span.
 */
export function WeatherWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(weatherFrameMessages);
  const fields = weatherFields(previewSource(frame));
  if (!hasWeather(fields)) {
    return <WidgetEmpty frame={frame} size={size} message={t("noWeather")} />;
  }
  const { place, temp, condition, days } = fields;
  const paint: WidgetPaint = {
    name: weatherSky(condition),
    background: cardPaint(frame).background,
  };
  const range = weatherRange(fields, t);
  const conditionIcon = inferConditionIcon(condition ?? "");
  const tempText = temp !== null && (
    <p
      className={cn("tabular-nums", WIDGET_DISPLAY.xl)}
      data-testid="weather-widget-temp"
    >
      {temp}°
    </p>
  );

  if (size === "small") {
    return (
      <WidgetFaceRoot frame={frame} size={size} paint={paint}>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <p className="truncate font-semibold">{place}</p>
          <DayIcon name={conditionIcon} className="h-4 w-4 shrink-0" />
        </div>
        {tempText}
        <Summary condition={condition} range={range} className="mt-auto" />
      </WidgetFaceRoot>
    );
  }

  if (size === "medium") {
    return (
      <WidgetFaceRoot
        frame={frame}
        size={size}
        paint={paint}
        className="grid grid-cols-2 gap-[18px]"
      >
        <div className="flex min-w-0 flex-col gap-1.5">
          <p className="truncate font-semibold">{place}</p>
          {tempText}
          <Summary condition={condition} range={range} className="mt-auto" />
        </div>
        {days.length > 0 && (
          <div
            className="flex justify-between self-end text-center text-appkit-caption"
            data-testid="weather-widget-days"
          >
            {days.slice(0, MEDIUM_DAYS).map((day, index) => (
              <div key={`${day.day}-${index}`} className="grid gap-0.5">
                <span className={WIDGET_MUTED}>{day.day}</span>
                {day.icon && (
                  <DayIcon
                    name={day.icon}
                    className="mx-auto my-1 h-[18px] w-[18px]"
                  />
                )}
                <span>{day.high}°</span>
                <span className={WIDGET_MUTED}>{day.low}°</span>
              </div>
            ))}
          </div>
        )}
      </WidgetFaceRoot>
    );
  }

  const min = Math.min(...days.map((day) => day.low));
  const max = Math.max(...days.map((day) => day.high));
  return (
    <WidgetFaceRoot frame={frame} size={size} paint={paint}>
      <div className="flex min-w-0 justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{place}</p>
          <div className="mt-1.5">{tempText}</div>
        </div>
        <div className="min-w-0 text-right">
          <DayIcon name={conditionIcon} className="ml-auto h-[30px] w-[30px]" />
          <Summary condition={condition} range={range} className="mt-2" />
        </div>
      </div>
      {days.length > 0 && (
        <>
          <WidgetRule className="mb-1.5 mt-2.5" />
          <div className="grid gap-[14px]" data-testid="weather-widget-days">
            {days.map((day, index) => (
              <div
                key={`${day.day}-${index}`}
                className="grid grid-cols-[40px_22px_30px_1fr_30px] items-center gap-2"
              >
                <span className="truncate">{day.day}</span>
                {day.icon ? (
                  <DayIcon name={day.icon} className="h-4 w-4" />
                ) : (
                  <span />
                )}
                <span className={cn("text-right tabular-nums", WIDGET_MUTED)}>
                  {day.low}°
                </span>
                <DayBar day={day} min={min} max={max} />
                <span className="tabular-nums">{day.high}°</span>
              </div>
            ))}
          </div>
        </>
      )}
    </WidgetFaceRoot>
  );
}
